export type ChatId = string;
export type ConversationId = string;

export type AdapterMessage = {
  id: string;
  role: "user" | "assistant";
  content: string;
  createdAt?: number;
};

export type ConversationSummary = {
  id: ConversationId;
  title: string;
  preview: string;
  updatedAt: number;
  source: string;
};

export type ModelCapability = {
  fast: boolean;
  reasoning: boolean;
  canDisableReasoning?: boolean;
};

export type ModelProvider = {
  slug: string;
  name: string;
  models: string[];
  capabilities: Record<string, ModelCapability>;
};

export type ModelCatalog = {
  provider: string;
  model: string;
  providers: ModelProvider[];
};

export type ActiveChat = {
  chatId: ChatId;
  conversationId: ConversationId;
  messages: AdapterMessage[];
  info?: Record<string, unknown>;
  running: boolean;
  status?: string;
  inflight?: unknown;
};

export type ChatEvent =
  | { type: "ready" }
  | { type: "disposed" }
  | { type: "disconnected"; payload: string }
  | { type: "delta" | "interim" | "reasoning"; chatId: ChatId; text: string }
  | { type: "complete"; chatId: ChatId; text: string; status: "complete" | "interrupted" | "error"; payload: Record<string, unknown> }
  | { type: "tool"; chatId: ChatId; phase: "start" | "complete"; payload: unknown }
  | { type: "status"; chatId: ChatId; payload: unknown }
  | { type: "approval" | "clarify"; chatId: ChatId; payload: Record<string, unknown> }
  | { type: "error"; chatId?: ChatId; payload: unknown };

type RpcEnvelope = {
  jsonrpc: "2.0";
  id?: number;
  method?: string;
  result?: unknown;
  error?: { code?: number; message?: string; data?: unknown };
  params?: { type?: string; session_id?: string; payload?: Record<string, unknown> };
};

type PendingRpc = {
  resolve: (value: unknown) => void;
  reject: (reason: Error) => void;
  timeout: number;
};

export type CreateOptions = {
  provider?: string;
  model?: string;
  reasoningEffort?: "none" | "minimal" | "low" | "medium" | "high" | "xhigh" | "max";
  fast?: boolean;
  hidden?: boolean;
  closeOnDisconnect?: boolean;
};

class AsyncEventQueue {
  private values: ChatEvent[] = [];
  private waiters: Array<(result: IteratorResult<ChatEvent>) => void> = [];

  push(value: ChatEvent) {
    const waiter = this.waiters.shift();
    if (waiter) waiter({ value, done: false });
    else this.values.push(value);
  }

  async next(): Promise<IteratorResult<ChatEvent>> {
    const value = this.values.shift();
    if (value) return { value, done: false };
    return new Promise((resolve) => this.waiters.push(resolve));
  }
}

export class AuthRequiredError extends Error {
  loginUrl: string;

  constructor(loginUrl = "/login") {
    super("로그인이 필요합니다.");
    this.name = "AuthRequiredError";
    this.loginUrl = loginUrl;
  }
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" ? value as Record<string, unknown> : {};
}

function asText(value: unknown): string {
  if (typeof value === "string") return value;
  if (Array.isArray(value)) {
    return value.map((part) => {
      if (typeof part === "string") return part;
      const record = asRecord(part);
      return typeof record.text === "string" ? record.text : "";
    }).join("");
  }
  const record = asRecord(value);
  return typeof record.text === "string" ? record.text : "";
}

function asTimestamp(value: unknown): number {
  if (typeof value === "number") return value > 10_000_000_000 ? value : value * 1000;
  if (typeof value === "string") {
    const numeric = Number(value);
    if (Number.isFinite(numeric) && numeric > 0) return numeric > 10_000_000_000 ? numeric : numeric * 1000;
    const parsed = Date.parse(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return Date.now();
}

function normalizeMessage(value: unknown, index: number): AdapterMessage | null {
  const record = asRecord(value);
  const rawRole = String(record.role ?? record.sender ?? "").toLowerCase();
  const role = rawRole === "user" || rawRole === "human" ? "user" : rawRole === "assistant" || rawRole === "ai" ? "assistant" : null;
  if (!role) return null;
  return {
    id: String(record.id ?? record.message_id ?? `${role}-${index}-${Date.now()}`),
    role,
    content: asText(record.content ?? record.text ?? record.message),
    createdAt: asTimestamp(record.created_at ?? record.timestamp ?? record.createdAt),
  };
}

function normalizeMessages(value: unknown): AdapterMessage[] {
  const array = Array.isArray(value) ? value : [];
  return array.map(normalizeMessage).filter((message): message is AdapterMessage => message !== null);
}

const lifeAuthenticatedApp = ["dash.limjaeyoung.com", "app.limjaeyoung.com", "chat.limjaeyoung.com", "localhost", "127.0.0.1"].includes(window.location.hostname);
const hermesApiRoot = lifeAuthenticatedApp ? "/api/chat" : "/api";
const hermesUrl = (path: string) => `${hermesApiRoot}${path}`;

function wsUrl(ticket?: string) {
  const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  const base = `${protocol}//${window.location.host}${hermesUrl("/ws")}`;
  return ticket ? `${base}?ticket=${encodeURIComponent(ticket)}` : base;
}

export class HermesChatClient {
  private socket: WebSocket | null = null;
  private socketReady = false;
  private connectionGeneration = 0;
  private requestId = 0;
  private pending = new Map<number, PendingRpc>();
  private queue = new AsyncEventQueue();

  async authenticate(): Promise<{ userId: string; email?: string; displayName?: string }> {
    const response = await fetch(lifeAuthenticatedApp ? "/api/life/auth/me" : hermesUrl("/auth/me"), { credentials: "include" });
    const payload = asRecord(await response.json().catch(() => ({})));
    if (response.status === 401) throw new AuthRequiredError(lifeAuthenticatedApp ? "/" : typeof payload.login_url === "string" ? payload.login_url : "/login");
    if (!response.ok) throw new Error(typeof payload.detail === "string" ? payload.detail : "인증 상태를 확인하지 못했습니다.");
    return {
      userId: String(payload.user_id ?? payload.subject ?? payload.id ?? "owner"),
      email: typeof payload.email === "string" ? payload.email : undefined,
      displayName: typeof payload.display_name === "string" ? payload.display_name : typeof payload.name === "string" ? payload.name : undefined,
    };
  }

  async connect(signal?: AbortSignal): Promise<void> {
    const generation = ++this.connectionGeneration;
    let ticket: string | undefined;
    if (!lifeAuthenticatedApp) {
      const ticketResponse = await fetch(hermesUrl("/auth/ws-ticket"), { method: "POST", credentials: "include", signal });
      const ticketPayload = asRecord(await ticketResponse.json().catch(() => ({})));
      if (ticketResponse.status === 401) throw new AuthRequiredError("/login");
      if (!ticketResponse.ok || typeof ticketPayload.ticket !== "string") throw new Error("Hermes 연결 티켓을 발급받지 못했습니다.");
      ticket = ticketPayload.ticket;
    }
    if (signal?.aborted || generation !== this.connectionGeneration) {
      throw new DOMException("연결이 취소되었습니다.", "AbortError");
    }

    this.detachSocket();

    await new Promise<void>((resolve, reject) => {
      const socket = new WebSocket(wsUrl(ticket));
      this.socket = socket;
      let ready = false;
      let settled = false;
      const timeout = window.setTimeout(() => {
        if (settled) return;
        settled = true;
        socket.close();
        reject(new Error("Hermes 연결 시간이 초과되었습니다."));
      }, 12_000);

      const abort = () => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeout);
        socket.close();
        reject(new DOMException("연결이 취소되었습니다.", "AbortError"));
      };
      signal?.addEventListener("abort", abort, { once: true });

      socket.onmessage = (event) => {
        let envelope: RpcEnvelope;
        try { envelope = JSON.parse(String(event.data)) as RpcEnvelope; }
        catch { return; }
        if (envelope.method === "event" && envelope.params?.type === "gateway.ready") {
          if (settled || this.socket !== socket || generation !== this.connectionGeneration) return;
          ready = true;
          settled = true;
          this.socketReady = true;
          window.clearTimeout(timeout);
          signal?.removeEventListener("abort", abort);
          this.queue.push({ type: "ready" });
          resolve();
          return;
        }
        this.handleEnvelope(envelope);
      };
      socket.onerror = () => {
        if (!settled) {
          settled = true;
          window.clearTimeout(timeout);
          socket.close();
          reject(new Error("Hermes WebSocket 연결에 실패했습니다."));
        }
      };
      socket.onclose = () => {
        window.clearTimeout(timeout);
        signal?.removeEventListener("abort", abort);
        const isCurrent = this.socket === socket && generation === this.connectionGeneration;
        if (isCurrent) {
          this.socket = null;
          this.socketReady = false;
          this.rejectPending(new Error("Hermes 연결이 종료되었습니다."));
        }
        if (!settled) {
          settled = true;
          reject(new Error("Hermes WebSocket 연결이 종료되었습니다."));
        } else if (ready && isCurrent) {
          this.queue.push({ type: "disconnected", payload: "Hermes 연결이 일시적으로 끊어졌습니다." });
        }
      };
    });
  }

  disconnect() {
    this.connectionGeneration += 1;
    this.detachSocket();
  }

  dispose() {
    this.disconnect();
    this.queue.push({ type: "disposed" });
  }

  isConnected() {
    return this.socketReady && this.socket?.readyState === WebSocket.OPEN;
  }

  async probe() {
    await this.rpc("session.active_list", {}, 8_000);
  }

  async *events(): AsyncIterable<ChatEvent> {
    while (true) yield (await this.queue.next()).value;
  }

  async list(options: { limit?: number; offset?: number } = {}): Promise<ConversationSummary[]> {
    const params = new URLSearchParams({
      limit: String(options.limit ?? 100),
      offset: String(options.offset ?? 0),
      order: "recent",
    });
    const response = await fetch(`${hermesUrl("/sessions")}?${params}`, { credentials: "include" });
    if (response.status === 401) throw new AuthRequiredError(lifeAuthenticatedApp ? "/" : "/login");
    if (!response.ok) throw new Error("대화 목록을 불러오지 못했습니다.");
    const payload = asRecord(await response.json());
    const sessions = Array.isArray(payload.sessions) ? payload.sessions : Array.isArray(payload.items) ? payload.items : [];
    return sessions.map((sessionValue, index) => {
      const session = asRecord(sessionValue);
      const id = String(session.id ?? session.session_id ?? session.session_key ?? "");
      const preview = asText(session.preview ?? session.last_message ?? session.summary);
      return {
        id,
        title: String(session.title ?? session.name ?? preview.slice(0, 32) ?? `대화 ${index + 1}`),
        preview,
        updatedAt: asTimestamp(session.updated_at ?? session.last_active ?? session.created_at ?? session.started_at),
        source: String(session.source ?? "other").trim().toLowerCase() || "other",
      };
    }).filter((session) => session.id);
  }

  async modelOptions(): Promise<ModelCatalog> {
    const response = await fetch(`${hermesUrl("/model/options")}?explicit_only=true`, { credentials: "include" });
    if (response.status === 401) throw new AuthRequiredError(lifeAuthenticatedApp ? "/" : "/login");
    if (!response.ok) throw new Error("사용 가능한 모델 목록을 불러오지 못했습니다.");
    const payload = asRecord(await response.json());
    const providers = (Array.isArray(payload.providers) ? payload.providers : []).map((providerValue) => {
      const provider = asRecord(providerValue);
      const rawCapabilities = asRecord(provider.capabilities);
      const capabilities: Record<string, ModelCapability> = {};
      for (const [model, capabilityValue] of Object.entries(rawCapabilities)) {
        const capability = asRecord(capabilityValue);
        capabilities[model] = {
          fast: capability.fast === true,
          reasoning: capability.reasoning !== false,
          canDisableReasoning: typeof capability.can_disable_reasoning === "boolean"
            ? capability.can_disable_reasoning
            : undefined,
        };
      }
      return {
        slug: String(provider.slug ?? ""),
        name: String(provider.name ?? provider.slug ?? ""),
        models: (Array.isArray(provider.models) ? provider.models : []).map(String),
        capabilities,
        authenticated: provider.authenticated !== false,
      };
    }).filter((provider) => provider.slug && provider.authenticated && provider.models.length)
      .map(({ authenticated: _authenticated, ...provider }) => provider);
    return {
      provider: String(payload.provider ?? ""),
      model: String(payload.model ?? ""),
      providers,
    };
  }

  async history(conversationId: ConversationId): Promise<AdapterMessage[]> {
    const params = new URLSearchParams({ limit: "500", order: "latest" });
    const response = await fetch(`${hermesUrl("/sessions")}/${encodeURIComponent(conversationId)}/messages?${params}`, { credentials: "include" });
    if (!response.ok) throw new Error("대화 내용을 불러오지 못했습니다.");
    const payload = asRecord(await response.json());
    return normalizeMessages(payload.messages);
  }

  async create(options: CreateOptions = {}): Promise<ActiveChat> {
    const result = asRecord(await this.rpc("session.create", {
      source: "webui",
      close_on_disconnect: options.closeOnDisconnect ?? false,
      provider: options.provider,
      model: options.model,
      reasoning_effort: options.reasoningEffort,
      fast: options.fast,
      hidden: options.hidden,
    }));
    return this.normalizeActiveChat(result);
  }

  async resume(conversationId: ConversationId): Promise<ActiveChat> {
    const result = asRecord(await this.rpc("session.resume", {
      session_id: conversationId,
      source: "webui",
      close_on_disconnect: false,
    }));
    return this.normalizeActiveChat(result, conversationId);
  }

  async send(chatId: ChatId, text: string) {
    await this.rpc("prompt.submit", { session_id: chatId, text });
  }

  async setModel(chatId: ChatId, input: { provider: string; model: string }) {
    const value = `${input.model} --provider ${input.provider} --session`;
    let result = asRecord(await this.rpc("config.set", {
      session_id: chatId,
      key: "model",
      value,
    }));
    if (result.confirm_required === true) {
      result = asRecord(await this.rpc("config.set", {
        session_id: chatId,
        key: "model",
        value,
        confirm_expensive_model: true,
      }));
    }
    return result;
  }

  async setReasoning(chatId: ChatId, effort: NonNullable<CreateOptions["reasoningEffort"]>) {
    return this.rpc("config.set", {
      session_id: chatId,
      key: "reasoning",
      value: effort,
      scope: "session",
    });
  }

  async stop(chatId: ChatId) {
    await this.rpc("session.interrupt", { session_id: chatId });
  }

  async close(chatId: ChatId) {
    await this.rpc("session.close", { session_id: chatId });
  }

  async attachImage(chatId: ChatId, input: { contentBase64: string; filename?: string }) {
    return this.rpc("image.attach_bytes", {
      session_id: chatId,
      content_base64: input.contentBase64,
      filename: input.filename,
    });
  }

  async answerClarify(chatId: ChatId, input: { requestId: string; answer: string; questionId?: string }) {
    return this.rpc("clarify.respond", { session_id: chatId, request_id: input.requestId, answer: input.answer, question_id: input.questionId });
  }

  async answerApproval(chatId: ChatId, input: { requestId: string; choice: "once" | "session" | "always" | "deny" }) {
    return this.rpc("approval.respond", { session_id: chatId, request_id: input.requestId, choice: input.choice });
  }

  private normalizeActiveChat(result: Record<string, unknown>, fallbackConversationId?: string): ActiveChat {
    const info = asRecord(result.info);
    const contract = Number(info.desktop_contract ?? 0);
    if (contract && contract < 6) throw new Error("이 Hermes 서버는 현재 웹 채팅과 호환되지 않습니다.");
    const chatId = String(result.session_id ?? "");
    const conversationId = String(result.stored_session_id ?? result.resumed ?? result.session_key ?? fallbackConversationId ?? "");
    if (!chatId || !conversationId) throw new Error("Hermes 세션 식별자를 받지 못했습니다.");
    return {
      chatId,
      conversationId,
      messages: normalizeMessages(result.messages),
      info,
      running: result.running === true || result.status === "streaming",
      status: typeof result.status === "string" ? result.status : undefined,
      inflight: result.inflight,
    };
  }

  private rpc(method: string, params: Record<string, unknown>, timeoutMs = 60_000): Promise<unknown> {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return Promise.reject(new Error("Hermes에 연결되어 있지 않습니다."));
    const id = ++this.requestId;
    return new Promise((resolve, reject) => {
      const timeout = window.setTimeout(() => {
        if (!this.pending.delete(id)) return;
        reject(new Error(`${method} 요청 시간이 초과되었습니다.`));
      }, timeoutMs);
      this.pending.set(id, { resolve, reject, timeout });
      this.socket?.send(JSON.stringify({ jsonrpc: "2.0", id, method, params }));
    });
  }

  private handleEnvelope(envelope: RpcEnvelope) {
    if (typeof envelope.id === "number") {
      const pending = this.pending.get(envelope.id);
      if (!pending) return;
      this.pending.delete(envelope.id);
      window.clearTimeout(pending.timeout);
      if (envelope.error) pending.reject(new Error(envelope.error.message ?? "Hermes 요청에 실패했습니다."));
      else pending.resolve(envelope.result);
      return;
    }
    if (envelope.method !== "event" || !envelope.params?.type) return;
    const type = envelope.params.type;
    const chatId = String(envelope.params.session_id ?? "");
    const payload = envelope.params.payload ?? {};
    if (type === "message.delta") this.queue.push({ type: "delta", chatId, text: asText(payload.text ?? payload.rendered) });
    else if (type === "message.interim") this.queue.push({ type: "interim", chatId, text: asText(payload.text) });
    else if (type === "reasoning.delta") this.queue.push({ type: "reasoning", chatId, text: asText(payload.text) });
    else if (type === "message.complete") this.queue.push({ type: "complete", chatId, text: asText(payload.text), status: (payload.status === "interrupted" || payload.status === "error") ? payload.status : "complete", payload });
    else if (type === "tool.start" || type === "tool.complete") this.queue.push({ type: "tool", chatId, phase: type.endsWith("start") ? "start" : "complete", payload });
    else if (type === "approval.request") this.queue.push({ type: "approval", chatId, payload });
    else if (type === "clarify.request") this.queue.push({ type: "clarify", chatId, payload });
    else if (type === "error") this.queue.push({ type: "error", chatId, payload });
    else if (type === "status.update" || type === "session.info") this.queue.push({ type: "status", chatId, payload });
  }

  private rejectPending(error: Error) {
    this.pending.forEach(({ reject, timeout }) => {
      window.clearTimeout(timeout);
      reject(error);
    });
    this.pending.clear();
  }

  private detachSocket() {
    const socket = this.socket;
    this.socket = null;
    this.socketReady = false;
    if (socket) {
      socket.onmessage = null;
      socket.onerror = null;
      socket.onclose = null;
      socket.close();
    }
    this.rejectPending(new Error("Hermes 연결이 종료되었습니다."));
  }
}
